import StandIcon                      from "@/assets/icons/StandIcon";
import List                           from "@/components/list/List";
import Spinner                        from "@/components/spinner/Spinner";
import StandMeModel                   from "@/models/services/website/StandMeModel";
import StandPageModel                 from "@/models/services/website/StandPageModel";
import StandPageValidationStatusModel from "@/models/services/website/StandPageValidationStatusModel";
import WebsiteEntertainmentModel      from "@/models/services/website/WebsiteEntertainmentModel";
import StandsListRow                  from "@/pages/Stands/components/StandsListRow";
import useFetch                       from "@widesk-core/hooks/useFetch";
import usePagedCollection             from "@widesk-core/hooks/usePagedCollection";
import ApiCollection                  from "@widesk-core/models/ApiCollection";
import PagedCollection                from "@widesk-core/models/PagedCollection";
import React                          from "react";
import { useTranslation }             from "react-i18next";

interface IStandsListBoxProps {
	websiteEntertainment: WebsiteEntertainmentModel;
}

export type StandListBoxRowData = {
	stand: StandMeModel;
	standPage: StandPageModel | null;
	standPageValidationStatus: StandPageValidationStatusModel | null;
};
export type StandListBoxData = StandListBoxRowData[];

const StandsListBox = (props: IStandsListBoxProps) => {
	const { websiteEntertainment } = props;

	const { t } = useTranslation();

	const standCollection = usePagedCollection(StandMeModel);
	const [standListBoxData, setStandListBoxData] = React.useState<StandListBoxData>([]);

	const { data: standPageValidationStatuses, isLoading: standPageValidationStatusesLoading } = useFetch(async () => {
		const collection = await new ApiCollection(StandPageValidationStatusModel).list();
		return collection.models.filter(m => ['IN_CREATION', 'REFUSED', 'VALIDATED', 'VALIDATION_REQUESTED'].includes(m.reference));
	}, true);

	if (standPageValidationStatusesLoading) {
		return <Spinner />;
	}

	return (
		<List
			afterFetch={async () => {
				const standPagesCollection = new PagedCollection(StandPageModel);
				await standPagesCollection
					.setSort('id', 'desc')
					.setFilter('stand', standCollection.models.map(s => s.id))
					.list();

				const data: StandListBoxData = standCollection.models.map(stand => {
					const standPage = standPagesCollection.models.find(sp => sp.getId('stand') === stand.id) ?? null;
					const statusId = standPage?.getId('standPageValidationStatus');
					const standPageValidationStatus = standPageValidationStatuses?.find(s => s.id === statusId) ?? null;
					return { stand, standPage, standPageValidationStatus };
				});

				setStandListBoxData(data);
			}}
			beforeFetch={() => {
				standCollection.setSort('updatedAt', 'desc');
			}}
			collection={standCollection}
			renderRow={(model) => {
				const rowData = standListBoxData.find(d => d.stand.id === model.id) as StandListBoxRowData;
				return <StandsListRow rowData={rowData} websiteEntertainment={websiteEntertainment} />;
			}}
			options={{
				clickable: true,
				emptyMessages: {
					title: t('pages.stands.list.empty.title'),
					description: t('pages.stands.list.empty.description'),
					icon: <StandIcon size="xl" />,
				},
				filters: {
					'standPages.standPageValidationStatus': standPageValidationStatuses?.map(m => m.id),
					'websiteEntertainment': websiteEntertainment.id,
				},
				itemsMargin: '10px 0px 10px 0px',
				itemsPadding: '10px',
				padding: '0px 10px 0px 10px',
				withSeparators: true,
			}}
		/>
	);
};

export default StandsListBox;
